import { useMemo, useState } from 'react';
import {
	SelectOption,
	SortOrder,
} from '../../../../../components/blackrock/advisor-center/select/SelectProps.ts';
import {
	sortByAuthor,
	sortByPublishedDate,
} from '../../../../../components/blackrock/advisor-center/cards/series-item-card/_helpers/series-item-helpers.ts';
import { SortTypes, SortTranslation } from '../model.ts';
import { DEFAULT_SORT, DEFAULT_SORT_ORDER } from '../dynamic-content.config.ts';
import { SeriesItemData } from '../../../../../components/blackrock/advisor-center/cards/series-item-card/SeriesItemCardProps.ts';

/**
 * Custom hook to sort SeriesItemData list.
 * @param {SeriesItemData[]} list - The series item list to sort.
 * @param {SortTranslation} translation - The translation data to init sort.
 * @param {boolean} sortDisabled - Is the sort functionality disabled?.
 * @returns {SeriesItemData[]} sortedList - The sorted list.
 * @returns {SortOption[]} sortOptions - The sort options for the Sort component.
 * @returns {void} onSelectNewOption - The function to set the selected sort option.
 */
export const useSort = (
	list: SeriesItemData[],
	translation: SortTranslation,
	sortDisabled?: boolean
): [
	SeriesItemData[],
	SelectOption[],
	(newOptionValue: string, order: SortOrder) => void,
] => {
	if (sortDisabled) {
		return [
			list,
			[],
			function () {
				return;
			},
		];
	} else {
		const [selectedSort, setSelectedSort] = useState<SortTypes>(DEFAULT_SORT);
		const [selectedSortOrder, setSelectedSortOrder] =
			useState<SortOrder>(DEFAULT_SORT_ORDER);
		const [sortOptions] = useState<SelectOption[]>(getSortOptions(translation));

		const sortedList = useMemo(
			() => getSortedList(list, selectedSort, selectedSortOrder),
			[list, selectedSort, selectedSortOrder]
		);

		const onSelectNewOption = (newOptionValue: string, order: SortOrder) => {
			setSelectedSort(newOptionValue as SortTypes);
			setSelectedSortOrder(order);
		};

		return [sortedList, sortOptions, onSelectNewOption];
	}
};

const getSortedList = (
	list: SeriesItemData[],
	selectedSort: SortTypes,
	selectedSortOrder: SortOrder
) => {
	switch (selectedSort) {
		case SortTypes.authors:
			return sortByAuthor([...list], selectedSortOrder);
		case SortTypes.publishedDate:
			return sortByPublishedDate([...list], selectedSortOrder);
	}
};

const getSortOptions = (translation: SortTranslation): SelectOption[] => {
	return [
		{
			label: translation.authors,
			value: SortTypes.authors,
		},
		{
			label: translation.publishedDate,
			value: SortTypes.publishedDate,
		},
	];
};
